import i18n from "i18next";

export interface FieldError {
  scope: string;
  field: string;
  message: string;
  errorCode: string;
  constraints?: string;
}

export interface ParsedResponse<T> {
  data: T | undefined;
  responseCode: number | undefined;
  errorCode?: string;
  errorMessage?: string;
  fieldErrors?: FieldError[];
  globalErrors?: FieldError[];
  attempts?: number;
  maxAttempts?: number;
  ok: boolean;
  headers?: Headers;
}

export interface ErrorResponse {
  type: string;
  title: string;
  status: number;
  detail?: string;
  details?: string;
  instance?: string;
  errorCategory?: string;
  errorCode?: string;
  invalidParams?: FieldError[];
  attempts?: number;
  maxAttempts?: number;
}

const safeParse = (data: string): any => {
  try {
    return JSON.parse(data);
    // eslint-disable-next-line
  } catch (e) {
    // If parsing fails, assume it's already a string and return it as-is
    return data;
  }
};

export class ServerResponseParser<T> {
  parseResponse(response: Response): Promise<ParsedResponse<T>> {
    if (response.ok) {
      if (response.status === 204) {
        return Promise.resolve({
          responseCode: response.status,
          data: undefined,
          ok: true,
          headers: response.headers,
        } as ParsedResponse<T>);
      }

      return response
        .clone()
        .text()
        .then((text: string) => {
          try {
            if (
              response.headers.get("Content-Type")?.includes("application/json")
            ) {
              const data = safeParse(text);
              return {
                responseCode: response.status,
                errorMessage: undefined,
                errorCode: undefined,
                data,
                ok: true,
                headers: response.headers,
              };
            } else {
              return response.blob().then((blob: Blob) => {
                return {
                  responseCode: response.status,
                  errorMessage: undefined,
                  errorCode: undefined,
                  data: blob,
                  ok: true,
                  headers: response.headers,
                };
              });
            }
          } catch (error) {
            console.error("Error parsing response:", error);
            return Promise.reject(error);
          }
        })
        .catch(() => {
          return {
            responseCode: response.status,
            errorMessage: "Error parsing response",
            errorCode: undefined,
            data: undefined,
            ok: false,
            headers: response.headers,
          };
        });
    } else {
      return response.text().then((text: string) => {
        const json = (text && safeParse(text)) as ErrorResponse;
        const result = {
          responseCode: response.status,
          data: json ? (json as T) : undefined,
          errorCode: json.errorCode,
          ok: false,
          attempts: json.attempts,
          maxAttempts: json.maxAttempts,
          headers: response.headers,
        };
        if (response.status === 400) {
          return {
            ...result,
            errorMessage: json.detail
              ? json.detail
              : i18n.t("error.bad-request"),
            fieldErrors: json.invalidParams?.filter(
              (error) => error.scope === "FIELD",
            ),
            globalErrors: json.invalidParams?.filter(
              (error) => error.scope === "GLOBAL",
            ),
            headers: response.headers,
          };
        } else {
          return {
            ...result,
            errorMessage: json.detail
              ? (json.title ? json.title + " : " : "") + json.detail
              : json.title,
          };
        }
      });
    }
  }
}
